import React, { useState, useEffect } from 'react';
import { Modal, Button, Dropdown } from 'pepsico-ds';
import { TextField, Tooltip, TextareaAutosize } from '@mui/material';
import { DeleteOutline } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { goalMetricOptions } from '../../utils/Constants';
import { convertStringToCamelCase } from '../../utils/Utils';
import './HomePage.css';

const GoalsModal = (props) => {
  const {
    showModal,
    handleClose,
    saveGoalInputs,
    goalInputs,
    setCycleGoalsDescription,
    cycleGoalsDescription,
    screenName = 'home',
    selectedCycleObj = {}
  } = props;
  let initialState = goalInputs?.length === 0 ? [{ id: 1 }] : goalInputs;
  const [goalFormInputs, setGoalFormInputs] = useState(initialState);
  const [goalDescription, setGoalsDescription] = useState(cycleGoalsDescription);

  const addGoalsForm = () => {
    if (goalFormInputs?.length < 5) {
      let ids = goalFormInputs?.map((item) => item.id);
      const maxId = ids?.length === 0 ? 0 : Math.max(...ids);
      setGoalFormInputs([...goalFormInputs, ...[{ id: maxId + 1 }]]);
    }
  };

  const handleDeleteGoalInput = (id) => {
    setGoalFormInputs(goalFormInputs?.filter((item) => item.id !== id));
  };

  const handleChange = async (value, id) => {
    let data = goalFormInputs;
    let filteredData = await data?.map((item) => {
      if (item.id === id) {
        if (value === undefined) {
          item = { id: item.id };
        } else {
          item = { id: item.id, [value]: Object.values(item)?.[1] || '' };
        }
      }
      return item;
    });
    setGoalFormInputs(filteredData);
  };

  const handleGoalInput = async (event, list) => {
    let data = goalFormInputs;
    let newValue = event.target.value;
    let filteredData = await data?.map((item) => {
      if (item.id === list?.id) {
        if (newValue === undefined) {
          item = { id: item.id };
        } else {
          let key = Object.keys(item)?.[1] || '';
          if (newValue !== '--' && /^[^a-zA-Z]*$/.test(newValue)) {
            if (newValue?.indexOf('-') === 0) {
              newValue = '-' + newValue?.replace(/[^0-9]/g, '');
            }
            item = {
              id: item.id,
              [key]: newValue
            };
          }
        }
      }
      return item;
    });
    setGoalFormInputs(filteredData);
  };

  const getSelectedValue = (value) => {
    return value ? [{ displayText: value, id: value }] : [];
  };

  const getNewFilteredOptions = (allOptions, itemObj) => {
    let data = goalFormInputs;
    let arr = [];
    data?.map((item) => {
      arr = [...arr, ...Object.keys(item)];
    });
    let filteredArr = arr?.filter((li) => li !== 'id');
    let value = Object.keys(itemObj)?.length > 1 ? Object.keys(itemObj)?.[1] : '';
    return [
      ...[{ displayText: value, id: value }],
      ...(allOptions?.filter((option) => !filteredArr?.includes(option.displayText)) ?? [])
    ]?.filter((item) => item.id !== '');
  };

  const handleSave = () => {
    if (screenName === 'home') {
      saveGoalInputs(goalFormInputs);
      setCycleGoalsDescription(goalDescription);
    } else {
      saveGoalInputs({ goalFormInputs: goalFormInputs, goalDescription: goalDescription });
      setGoalFormInputs([{ id: 1 }]);
      setGoalsDescription('');
    }
    handleClose();
  };

  const handleMetricsClear = () => {
    setGoalFormInputs([{ id: 1 }]);
    setGoalsDescription('');
    handleClose();
  };

  useEffect(() => {
    if (Object.keys(selectedCycleObj)?.length > 0) {
      let filteredMetrics = [];
      goalMetricOptions
        ?.map((item) => item?.displayText)
        ?.map((item, index) => {
          filteredMetrics.push({
            id: index,
            [item]: selectedCycleObj[convertStringToCamelCase(item)]
          });
          return item;
        });
      setGoalFormInputs(filteredMetrics);
      setGoalsDescription(selectedCycleObj?.cycleTarget);
    }
  }, []);

  const handleDisableSave = () => {
    let isdisable = false;
    let keys = [];
    let values = [];
    goalFormInputs?.map((item) => {
      keys = [...keys, Object.keys(item)];
      values = [...values, Object.values(item)];
      if (Object.keys(item)?.filter((li) => li !== '')?.length < 2) {
        isdisable = true;
      }
    });
    if (keys.flat()?.includes('') || values.flat()?.includes('')) {
      isdisable = true;
    }

    return isdisable;
  };
  let disableSave = handleDisableSave();
  return (
    <Modal
      className="goal-modal"
      showModal={showModal}
      closeOnOutsideClick={false}
      primaryButtonProps={{
        onClick: () => handleSave(),
        size: 'medium',
        text: 'Save',
        variant: 'primary',
        disabled: disableSave
      }}
      secondaryButtonProps={{
        onClick: () => handleMetricsClear(),
        size: 'medium',
        text: 'Close',
        variant: 'secondary'
      }}
      showAuxiliaryButton={false}
      showTertiaryButton={false}
      showLink={false}
      type={'S'}
      onCloseModal={() => handleClose()}>
      <div className="goal-modal-body">
        <h3 className="goal-modal-title">Set Goals</h3>
        <span className="goal-modal-info">
          These goals are only a guideline and will not impact the results of algorithm
        </span>
        <h3>Cycle Target</h3>
        <TextareaAutosize
          data-testid="goal-description"
          id="description"
          placeholder="Type here"
          value={goalDescription}
          onChange={(event) => {
            if (event.target.value?.length < 500) setGoalsDescription(event.target.value);
          }}
          rows={4}
          style={{
            width: '100%',
            minHeight: '124px',
            resize: 'both',
            boxSizing: 'border-box',
            borderColor: 'rgba(192, 192, 192, 1)'
          }}
        />
        <div className="key-metrics-text-container">
          <span>Key Metrics</span>
          <Tooltip
            title={<div className="goal-result-info-container">Metrics will be in percentage</div>}
            placement="right">
            <InfoOutlinedIcon className="goal-icon-info" />
          </Tooltip>
        </div>

        <table className="goals-table">
          {goalFormInputs?.map((item, index) => (
            <tr key={item?.id}>
              <td>
                <Dropdown
                  data-testid={`goal-metric-dropdown-${index}`}
                  className="goal-metric-dropdown"
                  childList={getNewFilteredOptions(goalMetricOptions, item)}
                  isClearable
                  placeholder="Select"
                  selection="single"
                  selectedValue={getSelectedValue(Object.keys(item)?.[1])}
                  setSelectedValue={(value) => handleChange(value?.[0]?.displayText, item?.id)}
                  style={{
                    margin: '0px 10px'
                  }}
                />
              </td>
              <td className="goal-input">
                <TextField
                  data-testid={`goal-input-value-${index}`}
                  className="goal-action-input"
                  value={item?.[Object.keys(item)?.[1]] || ''}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 32 // Adjust the height of the input here
                    }
                  }}
                  onChange={(event) => handleGoalInput(event, item)}
                />
                <DeleteOutline
                  data-testid="delete-goal"
                  className="goal-delete-icon"
                  onClick={() => handleDeleteGoalInput(item.id)}
                />
              </td>
            </tr>
          ))}
        </table>
        <Button
          data-testid="add-goal"
          size="medium"
          text="+ Add Metric"
          variant="tertiary"
          disabled={goalFormInputs?.length === 5}
          onClick={() => addGoalsForm()}
        />
      </div>
    </Modal>
  );
};

export default GoalsModal;
